import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrazeTracker } from '@styleseat/analytics/trackers/BrazeTracker';
import { isSocialBrowser } from '../../../modules/BrowserInfo';
import { PROFILE_CLIENT_VIEW_PROFESSION } from '../../../api/ProfileClientView/ProfileClientView';
import { markProSeenForNCD, updateUserStateWithLocalStorageNCDAttribution } from '../../../modules/newClientDelivery';
import { getProfileMetaInfo } from '../../../modules/provider/Profile';
import analytics from '../../../modules/analytics';
import * as Page from '../../../modules/PageMeta';
import store from '../../../store';
import {
  useAsyncEffect,
  useOnce,
  useSnackbar,
} from '../../../hooks';
import useParams from '../../../hooks/useParams';
import type { IRootDispatch, RootState } from '../../../store/models';
import {
  CompleteProfileData,
  ProfileEntry,
  UserData,
} from './ProProfile.model';
import { ProProfileMobileHeader } from '../../shared/providerProfile/ProfileEditMobileHeader';
import ProviderProfile from '../../shared/providerProfile/ProviderProfile';
import { InactiveProfile } from '../../shared/providerProfile/InactiveProfile/InactiveProfile';
import { selectors as userSelectors } from '../../../store/CurrentUser.model';
import { requireLogin } from '../../shared/login/SignUpLoginModal';
import nonCriticalException from '../../../modules/exceptionLogger';
import ResponsiveLayout from '../../layouts/ResponsiveLayout';
import { ProviderLayout } from '../../layouts/ProviderLayout';
import { useShouldShowClientNavBar } from '../../consumer/ClientNavBar/hooks';

import ProfileSchema from '../../shared/providerProfile/ProfileSchema';
import { displayReviewSubmittedSnackbar } from '../../shared/providerProfile/ProviderProfileContainer/ProviderProfileNotifications';
import {
  ERROR_ROUTE,
  PROFILE_ROUTE,
  SALON_ROUTE,
} from '../../../route-names';
import { ProviderProfileLoader } from '../../shared/providerProfile/ProviderProfileLoader';
import { NCDSource } from '../../../modules/newClientDelivery.types';
import { ClientProviderProfile } from '../../../api/ProfileClientView';
import { AuthOrigins } from '../../../modules/user/constants';

function Profile({
  provider,
  isOwnProfile,
}: { provider: ClientProviderProfile; isOwnProfile: boolean }) {
  const { id: providerId } = provider;
  const dispatch = useDispatch<IRootDispatch>();
  const snackbar = useSnackbar();
  const params = useParams() || {};
  const [paramsAdded, setParamsAdded] = useState<{ ncd_source: String; ncd_term: number }>();

  const routeName = useSelector<RootState, string>(
    state => state.route.name,
  ) || '';
  const {
    profile,
    galleryImages,
    serviceGroups,
    relatedSalons,
    reviews,
    ratingsSummary,
    schedule,
    bookmarked,
    marketplaceProfile,
  } = useSelector<RootState, CompleteProfileData>(
    store.select.proProfile.getCompleteProfile(providerId),
  ) || {};

  const isLoggedIn = useSelector<RootState, boolean>(userSelectors.isLoggedIn);

  useEffect(() => {
    const pageMetaInfo = getProfileMetaInfo(profile, galleryImages, {
      shareImage: params.share_image,
      shareTitle: params.share_title,
    });
    Page.setMeta(pageMetaInfo);
  }, [
    profile,
    galleryImages,
    params.share_image,
    params.share_title,
  ]);

  useEffect(() => {
    if (!params.ncd_source) {
      if (isSocialBrowser()) {
        dispatch.route.addParams({
          params: { ncd_source: NCDSource.SocialMedia },
          notify: false,
          replace: false,
          reload: false,
        });
      } else if (params.utm_medium?.toLowerCase() === NCDSource.Sem) {
        dispatch.route.addParams({
          params: { ncd_source: NCDSource.Sem, ncd_term: profile.id },
          notify: false,
          replace: false,
          reload: false,
        });
        setParamsAdded({ ncd_source: NCDSource.Sem, ncd_term: profile.id });
      }
    }
  }, [
    dispatch.route,
    params.ncd_source,
    params.utm_medium,
    profile.id,
  ]);

  useOnce(() => {
    analytics.track('client_proprofile_viewed', {
      ...params,
      search_id: params.sid,
      provider_id: profile.id,
      pro_id: profile.id,
      provider_id_viewed: profile.id,
      profile_type: 'provider',
      vanityUrl: profile.vanity_url,
    });
  }, (routeName === PROFILE_ROUTE) || null);

  // Handle NCD attribution storage for users visiting this pro

  useEffect(() => {
    markProSeenForNCD({ ...params, ...paramsAdded }, isLoggedIn, params?.sid, profile);
    if (isLoggedIn && profile?.id) {
      updateUserStateWithLocalStorageNCDAttribution(profile?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    profile?.id,
    paramsAdded,
  ]);

  useEffect(() => {
    // Set pixel id in the data layer so GTM can pick up on it
    if (profile?.pixel_id && window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push({ current_pro_pixel_id: profile?.pixel_id });

      // And fire an event because history change/page view will happen
      // before we can set the pixel ID
      window.dataLayer.push({ event: 'pixelIdLoaded' });
    }
  }, [profile?.pixel_id]);

  useEffect(() => {
    if (params?.isReviewSubmitted) {
      displayReviewSubmittedSnackbar(snackbar);
      analytics.track('client_proprofile_review_submitted_snack_shown');
    }
  }, [
    params?.isReviewSubmitted,
    snackbar,
  ]);

  const handleFavoritingProvider = useCallback(async () => {
    if (!isLoggedIn) {
      try {
        await requireLogin({
          mode: 'clientsignup',
          origin: AuthOrigins.ProfileHeaderIconFavPro,
          message: 'Sign up or log in to bookmark this profile',
        });
      } catch (e) {
        nonCriticalException(e);
      }
    }
    dispatch.favorites.toggleFavoritedProvider(profile?.id);
  }, [
    isLoggedIn,
    profile?.id,
    dispatch.favorites,
  ]);

  if (profile?.is_disabled) {
    return (
      <InactiveProfile
        profile={profile}
        proSuggestions={relatedSalons}
      />
    );
  }

  return profile ? (
    <ProviderProfile
      profile={profile}
      reviews={reviews}
      ratingsSummary={ratingsSummary}
      schedule={schedule}
      serviceGroups={serviceGroups}
      galleryImages={galleryImages}
      bookmarked={bookmarked}
      onBookmarkClick={handleFavoritingProvider}
      marketplaceProfile={marketplaceProfile}
      isOwnProfile={isOwnProfile}
    />
  ) : null;
}

/**
 * This is meant to be a temporary component that decides when to redirect to the Angular profile
 * Anyone that has editing abilities on the profile should get the old experience
 */
const ProfileRouter = () => {
  const params = useParams() || {};
  const { providerId: providerIdOrVanityUrl } = params;
  const dispatch = useDispatch<IRootDispatch>();
  const providerData = useSelector<RootState, ProfileEntry>(
    store.select.proProfile.byIdOrVanity({ providerId: providerIdOrVanityUrl }),
  );

  const {
    serviceGroups,
  } = providerData || {};

  const {
    userId,
    hasEditPrivilege: isUserEditable,
    isAPro,
  } = useSelector<RootState, UserData>((
    store.select.proProfile.userData({ providerId: providerData?.profile?.id })
  ));

  const {
    profile,
    error: profileError,
  } = providerData || {};

  const {
    shouldShowClientNav,
  } = useShouldShowClientNavBar();

  useAsyncEffect(async () => {
    const brazeTracker = (await analytics.getTracker('braze')) as BrazeTracker;
    if (!isUserEditable && brazeTracker && !brazeTracker.isInitialized) {
      brazeTracker.initializeBraze();
      brazeTracker.openSession();
    }
  }, [isUserEditable]);

  useEffect(() => {
    if (providerIdOrVanityUrl) {
      dispatch.proProfile.land({
        providerId: providerIdOrVanityUrl,
        skipCache: isAPro,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerIdOrVanityUrl]);

  useEffect(() => {
    if (!profile && profileError) {
      dispatch.route.go({
        replace: true,
        route: ERROR_ROUTE,
      });
    }

    if (profile?.profession === PROFILE_CLIENT_VIEW_PROFESSION.SALON) {
      dispatch.route.go({
        route: SALON_ROUTE,
        params: { salonId: profile.id },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profile,
    profileError,
  ]);

  useEffect(() => {
    dispatch.proProfile.userChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!profile) {
    return <ProviderProfileLoader />;
  }

  if (isUserEditable) {
    return (
      <ProviderLayout
        providerId={profile.id}
        renderMobileOnlyHeader={() => (
          <ProProfileMobileHeader providerId={profile.id} isEditMode={false} />
        )}
      >
        <ProfileSchema
          profile={profile}
          serviceGroups={serviceGroups}
        />
        <Profile provider={profile} isOwnProfile />
      </ProviderLayout>
    );
  }

  return (
    <ResponsiveLayout
      showClientNavBar={shouldShowClientNav}
    >
      <ProfileSchema
        profile={profile}
        serviceGroups={serviceGroups}
      />
      <Profile provider={profile} isOwnProfile={false} />
    </ResponsiveLayout>
  );
};

export default ProfileRouter;
